import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { apiClient } from '@/lib/api-client';
import type { CommandItem } from '@/shell/command/command-items';
import { useAdministrationScopesOptional } from '../place/AdministrationScopesProvider';
import { apiPlace, placeKey, sectionPath, type Place, type PlaceCan } from '../place/place';
import { placeFromPath } from '../place/place-from-path';
import { canAt } from '../place/scopes';

/** Fewer letters than this match too many people to be worth a request. */
const MIN_QUERY = 2;

type People = {
  readonly for: string;
  readonly rows: readonly { id: string; displayName: string; email: string }[];
} | null;

function usePeopleSearch(
  open: boolean,
  placeKind: 'tenants' | 'organizations' | undefined,
  placeId: string | undefined,
  canManage: boolean,
  needle: string,
) {
  const [people, setPeople] = useState<People>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || placeKind === undefined || placeId === undefined || !canManage) {
      return;
    }
    if (needle.length < MIN_QUERY) {
      setPeople(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      void apiClient
        .GET('/v1/administration/{placeKind}/{placeId}/people', {
          params: {
            path: { placeKind, placeId },
            query: { q: needle, status: 'active', sort: 'name' },
          },
        })
        .then(
          ({ data, response }) => {
            if (cancelled) {
              return;
            }
            setLoading(false);
            setPeople({
              for: needle,
              rows:
                data !== undefined && response.ok
                  ? data.items.flatMap((item) => (item.kind === 'user' ? [item] : []))
                  : [],
            });
          },
          () => {
            if (!cancelled) {
              setLoading(false);
              setPeople({ for: needle, rows: [] });
            }
          },
        );
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, placeKind, placeId, canManage, needle]);
  return { people, loading };
}

type BuildArgs = {
  readonly place: Place | null;
  readonly can: PlaceCan | null;
  readonly placeName: string;
  readonly people: People;
  readonly needle: string;
  readonly t: TFunction<'administration'>;
  readonly go: (path: string) => void;
};

function buildItems({ place, can, placeName, people, needle, t, go }: BuildArgs): CommandItem[] {
  // The platform has no Utenti or Ruoli e accessi in this slice: its acts would lead nowhere.
  if (place === null || place.kind === 'platform' || can === null || placeName === '') {
    return [];
  }
  const group = t('command.group', { place: placeName });
  const rows: CommandItem[] = [];
  if (can.invite) {
    rows.push({
      id: `admin:invite:${placeKey(place)}`,
      label: t('command.invite', { place: placeName }),
      group,
      kind: 'run',
      perform: () => go(`${sectionPath(place, 'people')}?invite=1`),
    });
  }
  if (can.grantRoles) {
    rows.push({
      id: `admin:grant:${placeKey(place)}`,
      label: t('command.grant', { place: placeName }),
      group,
      kind: 'run',
      perform: () => go(`${sectionPath(place, 'roles')}?grant=1`),
    });
  }
  // Rows from an older query are dropped rather than shown against the new one.
  if (people !== null && people.for === needle) {
    const peopleGroup = t('command.people', { place: placeName });
    for (const person of people.rows) {
      rows.push({
        id: `admin:person:${person.id}`,
        label: person.displayName,
        hint: person.email,
        group: peopleGroup,
        kind: 'navigate',
        perform: () => go(`${sectionPath(place, 'people')}/${encodeURIComponent(person.id)}`),
      });
    }
  }
  return rows;
}

type Administration = NonNullable<ReturnType<typeof useAdministrationScopesOptional>>;

function canHere(
  place: Place | null,
  administration: Administration | undefined,
  detail: Administration['places'][string] | undefined,
): PlaceCan | null {
  if (place === null || administration === undefined) return null;
  return detail?.can ?? canAt(place, administration.scopes, detail?.tenant?.id ?? null);
}

/** The place the address names, what this caller may do there, and its API pair. */
function useLocatedPlace() {
  const { pathname } = useLocation();
  const administration = useAdministrationScopesOptional();
  const located = placeFromPath(pathname);
  const place = located?.place ?? null;
  const detail = place === null ? undefined : administration?.places[placeKey(place)];
  const can = canHere(place, administration, detail);
  const placeName = detail?.place.name ?? '';
  const pair = place === null ? null : apiPlace(place);
  const placeKind = pair?.placeKind;
  const placeId = pair?.placeId;
  return { place, can, placeName, placeKind, placeId };
}

/**
 * The quick search inside the administration area (FR-UX-2): the place's own acts, «Invita persona»
 * and «Concedi ruolo», where the caller may use them, and — once something is typed — the people of
 * the place, searched by the server. Outside the area it adds nothing. Each row opens the section,
 * with its panel open where the act has one.
 */
export function useAdminCommandItems(
  open: boolean,
  query: string,
  go: (path: string) => void,
): { readonly items: readonly CommandItem[]; readonly loading: boolean } {
  const { t } = useTranslation('administration');
  const { place, can, placeName, placeKind, placeId } = useLocatedPlace();
  const needle = query.trim();
  const { people, loading } = usePeopleSearch(
    open,
    placeKind,
    placeId,
    can?.managePeople === true,
    needle,
  );

  const items = useMemo<CommandItem[]>(
    () => buildItems({ place, can, placeName, people, needle, t, go }),
    [place, can, placeName, people, needle, t, go],
  );

  return { items, loading };
}
